<!-- TODO: Refactor edit/create logic in separate components -->
<script lang="ts" setup name="RecipientListSailingScheduleTradeLaneCollapsible">
import { computed, type Ref, ref } from 'vue';
import { useVModel } from '@vueuse/core';

import { useShowModal } from '~/features/useModalsProvider';
import { useService } from '~/features/useService.js';

import type { HTTP } from '~/services/HTTP';
import Service from '~/services/Service.js';

import BaseButton from '~/components/BaseButton.vue';
import BaseCollapsible from '~/components/BaseCollapsible.vue';
import SailingScheduleTradeLane from '~/components/SailingScheduleTradeLane.vue';
import SailingScheduleTradeLaneVoyages from '~/components/SailingScheduleTradeLaneVoyages.vue';

import type {
  BaseTagContrastType,
  ISailingScheduleDraggableOption,
  ISailingScheduleTradeLaneSettings,
  ITradeLanePort,
  ITradeLaneVessel,
  SailingScheduleTradeLane as TradeLane,
  SailingScheduleV2Response,
} from '~/types';

import SailingScheduleTradeLaneActions from './SailingScheduleTradeLaneActions.vue';
import SailingScheduleTradeLaneSettingsModal from './SailingScheduleTradeLaneSettingsModal.vue';

const props = defineProps<{
  settings: ISailingScheduleTradeLaneSettings;
  tradeLane: TradeLane;
  tagVariant: BaseTagContrastType;
  voyageService: HTTP;
  color: string;
  isEdit: boolean;
  initialTradeList: string[];
}>();

const emit = defineEmits<{
  'update:settings': [ISailingScheduleTradeLaneSettings];
  'update:initialTradeList': string[];
}>();

const settings = useVModel(props, 'settings', emit);
const initialTradeList = useVModel(props, 'initialTradeList', emit);

const tradeLaneName = props.tradeLane.tradelane;
const showModal = useShowModal();

const hiddenCountLabel = (items: any) => {
  const count = items.filter((item) => !item.visible).length;
  return count ? `(${count})` : '';
};

const stateIsIdle = computed(() =>
  props.isEdit
    ? serviceState.stateIsIdle && defaultServiceState.stateIsIdle
    : serviceState.stateIsIdle,
);

const dataLoaded = ref(false);

const originalVessels = ref<ITradeLaneVessel[]>([]);
const initialVessels = ref<ITradeLaneVessel[]>([]);
const vessels = ref<ITradeLaneVessel[]>([]);

const originalPorts = ref<ITradeLanePort[]>([]);
const initialPorts = ref<ITradeLanePort[]>([]);
const ports = ref<ITradeLanePort[]>([]);

const hiddenPortsLabel = computed(() => hiddenCountLabel(ports.value));
const hiddenVesselsLabel = computed(() => hiddenCountLabel(vessels.value));

const vesselSettingsApplied = computed(
  () => JSON.stringify(vessels.value) !== JSON.stringify(originalVessels.value),
);

const portSettingsApplied = computed(
  () => JSON.stringify(ports.value) !== JSON.stringify(originalPorts.value),
);

const applyTradeLaneVisibility = (data: {
  ports: ITradeLanePort[];
  vessels: ITradeLaneVessel[];
}) => {
  ports.value = addVisibility(data.ports, settings.value[tradeLaneName]?.ports);

  vessels.value = addVisibility(
    data.vessels,
    settings.value[tradeLaneName]?.voyages,
  );
};

const defaultServiceState = useService(
  Service.sailingSchedule().voyages(tradeLaneName).index(),
  ({ data }: SailingScheduleV2Response) => {
    originalVessels.value = data.vessels;
    originalPorts.value = data.ports;

    if (settings.value[tradeLaneName]?.ports?.length === 0) {
      ports.value = addVisibility(
        data.ports,
        settings.value[tradeLaneName]?.ports,
      );
    }

    if (settings.value[tradeLaneName]?.voyages?.length === 0) {
      vessels.value = addVisibility(
        data.vessels,
        settings.value[tradeLaneName]?.voyages,
      );
    }

    return true;
  },
  { preventFetch: true, state: 'IDLE' },
);

const serviceState = useService(
  props.voyageService,
  ({ data }: SailingScheduleV2Response) => {
    if (props.isEdit) {
      if (
        !settings.value[tradeLaneName]?.ports ||
        settings.value[tradeLaneName]?.ports.length > 0
      ) {
        ports.value = addVisibility(
          data.ports,
          settings.value[tradeLaneName]?.ports,
        );
      }

      if (
        !settings.value[tradeLaneName]?.voyages ||
        settings.value[tradeLaneName]?.voyages.length > 0
      ) {
        vessels.value = addVisibility(
          data.vessels,
          settings.value[tradeLaneName]?.voyages,
        );
      }

      initialVessels.value = data.vessels;
      initialPorts.value = data.ports;
      return true;
    }

    originalVessels.value = data.vessels;
    originalPorts.value = data.ports;

    initialVessels.value = data.vessels;
    initialPorts.value = data.ports;

    applyTradeLaneVisibility(data);

    return true;
  },
  { preventFetch: true, state: 'IDLE' },
);

const addVisibility = <T extends ITradeLaneVessel | ITradeLanePort>(
  items: T[],
  settings?: { id: string; visible: boolean }[],
) => {
  const portsWithVisible = items.map((item) => {
    const isVisible =
      settings?.find((setting) => item.id === setting.id)?.visible ??
      item.visible;

    return {
      ...item,
      visible: isVisible,
    };
  });

  return portsWithVisible;
};

const onUpdate = <T extends ITradeLaneVessel | ITradeLanePort>({
  data,
  options,
  settingsType,
  originalData,
  initialData,
}: {
  data: Ref<T[]>;
  initialData: T[];
  originalData: T[];
  options: ISailingScheduleDraggableOption[];
  settingsType: 'ports' | 'voyages';
}) => {
  const updatedData = options
    .map(({ id, visible }) => {
      const originalValue = data.value.find((p) => p.id === id);
      return originalValue ? { ...originalValue, visible } : null;
    })
    .filter(Boolean) as T[];

  data.value = updatedData;

  if (!settings.value[tradeLaneName]) {
    settings.value[tradeLaneName] = {
      [settingsType]: [],
    };
  }

  const isUpdatedChanged =
    JSON.stringify(updatedData) !== JSON.stringify(originalData);
  const inInitialChanged =
    JSON.stringify(updatedData) !== JSON.stringify(initialData);

  if (isUpdatedChanged) {
    settings.value[tradeLaneName][settingsType] = options.map(
      ({ id, visible }) => ({
        id,
        visible,
      }),
    );

    return;
  }

  if (props.isEdit && inInitialChanged) {
    settings.value[tradeLaneName][settingsType] = [];
    return;
  }

  const hasNoSettingsApplied =
    Object.keys(settings.value[tradeLaneName]).filter(
      (key) => key !== settingsType,
    ).length === 0;

  if (hasNoSettingsApplied) {
    delete settings.value[tradeLaneName];
    return;
  }

  delete settings.value[tradeLaneName][settingsType];
};

const showVoyages = () => {
  if (dataLoaded.value) return;

  if (!props.isEdit) {
    serviceState.fetchData();
  }

  if (props.isEdit) {
    serviceState.fetchData();
    defaultServiceState.fetchData();
  }

  dataLoaded.value = true;
};

const onHidePorts = async () => {
  const result = await showModal(SailingScheduleTradeLaneSettingsModal, {
    options: ports.value.map((port) => ({
      id: port.id,
      label: port.name,
      visible: port.visible,
    })),
    title: 'Hide or Show Ports',
    placeholder: 'Search By Port Name',
    noResultsMessage:
      'Please clear the search field to see all available ports.',
  });

  if (result.action !== 'SAVE') return;

  onUpdate({
    data: ports,
    options: result.payload,
    settingsType: 'ports',
    originalData: originalPorts.value,
    initialData: initialPorts.value,
  });
};

const onHideVoyages = async () => {
  const result = await showModal(SailingScheduleTradeLaneSettingsModal, {
    options: vessels.value.map((vessel) => ({
      id: vessel.id,
      label: `${vessel.vessel} ${vessel.vvn || ''}`,
      visible: vessel.visible,
    })),
    title: 'Hide or Show Voyages',
    placeholder: 'Search By Vessel Name',
    noResultsMessage:
      'Please clear the search field to see all available vessels.',
  });

  if (result.action !== 'SAVE') return;

  onUpdate({
    data: vessels,
    options: result.payload,
    settingsType: 'voyages',
    originalData: originalVessels.value,
    initialData: initialVessels.value,
  });
};

const onResetSettings = () => {
  vessels.value = [...originalVessels.value];
  initialVessels.value = [...originalVessels.value];

  ports.value = [...originalPorts.value];
  initialPorts.value = [...originalPorts.value];

  if (initialTradeList.value.includes(tradeLaneName)) {
    settings.value[tradeLaneName] = {
      voyages: [],
      ports: [],
    };

    return;
  }

  delete settings.value[tradeLaneName];
};
</script>

<template>
  <div class="w-full rounded drop-shadow">
    <BaseCollapsible @open="showVoyages">
      <template #trigger="{ toggle, isOpen }">
        <SailingScheduleTradeLane
          v-bind="tradeLane"
          :color="color"
          :is-open="isOpen"
          :tagVariant="tagVariant"
          :class="{
            'rounded hover:drop-shadow-2': !isOpen,
            'rounded-b-none rounded-t': isOpen,
          }"
          @click="toggle"
        >
          <template #actions>
            <SailingScheduleTradeLaneActions :idle="stateIsIdle">
              <BaseButton
                :variant="
                  stateIsIdle && portSettingsApplied
                    ? 'btn-primary'
                    : 'btn-secondary'
                "
                icon="g2o:tune-rounded"
                @click.stop="onHidePorts"
              >
                Hide ports {{ hiddenPortsLabel }}
              </BaseButton>
              <BaseButton
                :variant="
                  stateIsIdle && vesselSettingsApplied
                    ? 'btn-primary'
                    : 'btn-secondary'
                "
                icon="g2o:tune-rounded"
                @click.stop="onHideVoyages"
              >
                Hide voyages {{ hiddenVesselsLabel }}
              </BaseButton>

              <BaseButton
                v-if="
                  stateIsIdle && (vesselSettingsApplied || portSettingsApplied)
                "
                variant="btn-tertiary"
                danger
                @click.stop="onResetSettings"
              >
                Reset list
              </BaseButton>
            </SailingScheduleTradeLaneActions>
          </template>
        </SailingScheduleTradeLane>
      </template>

      <SailingScheduleTradeLaneVoyages
        :service-state="serviceState"
        :vessels="vessels"
        :ports="ports"
        dateType="eta"
      />
    </BaseCollapsible>
  </div>
</template>
